import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ShiftEnum } from 'server/shared/models/shift.enum';
import { queryParam$ } from '../../../helpers/queryParam$';

@Component({
  selector: 'app-shift-select',
  templateUrl: './shift-select.component.html',
  styleUrls: ['./shift-select.component.scss'],
})
export class ShiftSelectComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();
  private suppressParamSync = false;

  @Input() useQueryParam?: string;
  @Input() label?: string;
  @Input() disabled = false;
  @Input() required = false;

  shiftEnum = ShiftEnum;
  selectedShifts: (ShiftEnum | 'ALL')[] = [];
  allShifts: ShiftEnum[] = [];

  constructor(private route: ActivatedRoute, private router: Router) {}

  ngOnInit(): void {
    // Get all shift values
    this.allShifts = Object.keys(ShiftEnum)
      .filter((key) => !isNaN(Number(ShiftEnum[key as keyof typeof ShiftEnum])))
      .map((key) => ShiftEnum[key as keyof typeof ShiftEnum]);

    if (this.useQueryParam) {
      queryParam$(this.route, this.useQueryParam)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((param) => {
          if (!this.suppressParamSync) {
            if (param) {
              this.selectedShifts = param
                .split(',')
                .map((shift) => {
                  if (shift === 'ALL') return 'ALL';
                  return ShiftEnum[shift as keyof typeof ShiftEnum];
                })
                .filter((shift) => shift !== undefined);
            } else {
              // Default to ALL selected
              this.selectedShifts = ['ALL'];
            }
          }
        });
    } else {
      // Default to ALL selected
      this.selectedShifts = ['ALL'];
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onSelectionChange(selectedShifts: (ShiftEnum | 'ALL')[]) {
    this.suppressParamSync = true;

    // If 'ALL' is currently selected and user selects a specific, remove 'ALL'
    if (this.selectedShifts.includes('ALL') && selectedShifts.length > 1) {
      selectedShifts = selectedShifts.filter((s) => s !== 'ALL');
    }

    // Mutually exclusive logic for 'ALL'
    if (selectedShifts.includes('ALL')) {
      this.selectedShifts = ['ALL'];
    } else {
      this.selectedShifts = selectedShifts.filter((s) => s !== 'ALL');
    }

    if (this.useQueryParam) {
      const paramValue =
        this.selectedShifts.length > 0
          ? this.selectedShifts
              .map((shift) =>
                shift === 'ALL' ? 'ALL' : ShiftEnum[shift as ShiftEnum],
              )
              .join(',')
          : null;
      this.updateQueryParam(paramValue);
    }

    setTimeout(() => {
      this.suppressParamSync = false;
    }, 0);
  }

  private updateQueryParam(paramValue: string | null) {
    const queryParams: any = {};
    queryParams[this.useQueryParam || ''] = paramValue;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }
}
